// 상위 payer 의 인게임 닉네임을 백그라운드로 채워 넣는 훅.
//
// 표를 막지 않는다 — 매출 표는 즉시 그려지고 닉네임만 나중에 채워진다.
// 이미 조회한 (verse, 지갑) 은 다시 조회하지 않는다(기간 필터를 바꿔도 재조회 없음).

import { useCallback, useEffect, useRef, useState } from 'react';
import { readAuthToken } from '@/auth/auth-token';
import { nicknameKey, resolveGameNicknames, type NicknameTarget } from '@/shop/game-nickname';
import { createVerseSessionPool } from '@/shop/verse-session-pool';

export type NicknamePhase = 'idle' | 'loading' | 'done' | 'error';

export interface GameNicknamesState {
  /** nicknameKey(verseId, wallet) → "Name#0000" */
  nicknames: Map<string, string>;
  phase: NicknamePhase;
  progress: { done: number; total: number } | null;
  /** 조회 실패 건수(부분 실패). */
  failed: number;
  error: string;
  /** 실패분 포함 재조회. */
  retry: () => void;
}

export interface UseGameNicknamesParams {
  /** 닉네임을 붙일 payer 들 — 보통 topPayers. */
  targets: NicknameTarget[];
  /** 어드민 지갑(연결 주체). 없으면 조회하지 않는다. */
  account: string | null;
  gatewayUrl: string;
}

export function useGameNicknames(params: UseGameNicknamesParams): GameNicknamesState {
  const { targets, account, gatewayUrl } = params;
  const [nicknames, setNicknames] = useState<Map<string, string>>(new Map());
  const [phase, setPhase] = useState<NicknamePhase>('idle');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failed, setFailed] = useState(0);
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);

  // 이미 시도한 키 — 게임 상태가 없는(다른 verse) payer 를 매번 다시 묻지 않기 위해 필요.
  const attempted = useRef(new Set<string>());

  const retry = useCallback(() => {
    attempted.current = new Set();
    setRetryToken((n) => n + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: retryToken 은 재조회 트리거 전용 의존성
  useEffect(() => {
    if (!account) return;
    const pending = targets.filter(
      (t) => t.wallet && !attempted.current.has(nicknameKey(t.verseId, t.wallet))
    );
    if (pending.length === 0) return;

    const controller = new AbortController();
    const pool = createVerseSessionPool({
      account,
      getToken: readAuthToken,
      gatewayUrl,
    });

    setPhase('loading');
    setError('');
    setProgress({ done: 0, total: pending.length });

    void (async () => {
      try {
        const res = await resolveGameNicknames(pending, pool.fetchState, {
          signal: controller.signal,
          onProgress: (p) => {
            if (!controller.signal.aborted) setProgress(p);
          },
        });
        if (controller.signal.aborted) return;
        for (const t of pending) attempted.current.add(nicknameKey(t.verseId, t.wallet));
        setNicknames((prev) => new Map([...prev, ...res.nicknames]));
        setFailed(res.failed);
        setPhase('done');
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
        setPhase('error');
      } finally {
        await pool.close();
      }
    })();

    return () => controller.abort();
  }, [targets, account, gatewayUrl, retryToken]);

  return { nicknames, phase, progress, failed, error, retry };
}
